'use client'

import { Button, Cell, InputText, List, Radio, Spacer, Text, Validation } from '@vinted/web-ui'
import { camelCase } from 'lodash'
import { useCallback, useEffect, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'

import {
  businessAccountFromUserDto,
  addEprInfo,
  EprCategoryDto,
  EprCountryDto,
  getEprCategories,
  getEprCountries,
  EprCountryModel,
  transformCategoriesDto,
  transformCountriesDto,
  clickEvent,
} from '@marketplace-web/business-accounts/business-accounts-data'
import { ErrorItem, HttpStatus } from '@marketplace-web/core-api/api-client-util'
import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session-data'
import {
  InputDropdown,
  InputDropdownRenderProps,
} from '@marketplace-web/common-components/input-dropdown-ui'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import {
  renderValidation,
  useFormValidationMessage,
} from '@marketplace-web/vendor-abstractions/react-hook-form-feature'

import { isValueInObject } from '../../../utils/object'

type Props = {
  onCancel: () => void
  onClose: () => void
  refetch: () => void
}

type CategoryOptions = {
  id: string
  title: string
  examples: string
}

type AddCountryUinFormData = {
  country: string
  category: string
  uin: string
}

const isDomesticEpr = true

enum AddUinField {
  Country = 'country',
  Category = 'category',
  Uin = 'uin',
}

interface EprInfoModel {
  epr_uin: {
    category: string
    uin: string
  }
}

const AddCountryUinForm = ({ onCancel, onClose, refetch }: Props) => {
  const [isCategoriesLoading, setIsCategoriesLoading] = useState(false)
  const [isCountriesLoading, setIsCountriesLoading] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showGeneralError, setShowGeneralError] = useState(false)
  const [categories, setCategories] = useState<Array<EprCategoryDto>>([])
  const [countries, setCountries] = useState<Array<EprCountryDto>>([])
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null)

  const inputDropdownTranslate = useTranslate()

  const { user: currentUser } = useSession()

  const formMethods = useForm<AddCountryUinFormData>({
    defaultValues: {
      country: '',
      category: '',
      uin: '',
    },
    mode: 'onChange',
  })

  const {
    register,
    setValue,
    control,
    watch,
    handleSubmit,
    setError,
    formState: { errors },
  } = formMethods

  const fetchCategories = useCallback(async () => {
    setIsCategoriesLoading(true)

    const categoriesResponse = await getEprCategories()

    if ('errors' in categoriesResponse) {
      setIsCategoriesLoading(false)
      setShowGeneralError(true)

      return
    }

    setCategories(transformCategoriesDto(categoriesResponse.epr_categories))

    setIsCategoriesLoading(false)
  }, [])

  const fetchCountries = useCallback(async () => {
    setIsCountriesLoading(true)

    const countriesResponse = await getEprCountries()

    if ('errors' in countriesResponse) {
      setIsCountriesLoading(false)
      setShowGeneralError(true)

      return
    }

    setCountries(transformCountriesDto(countriesResponse))

    setIsCountriesLoading(false)
  }, [])

  useEffect(() => {
    setShowGeneralError(false)
    fetchCategories()
    fetchCountries()
  }, [fetchCategories, fetchCountries])

  const setBackendErrors = (fieldErrors: Array<ErrorItem>) => {
    fieldErrors.forEach(({ field, value }) => {
      const fieldName = camelCase(field)

      if (isValueInObject(fieldName, AddUinField)) {
        setError(fieldName, { type: 'manual', message: value })
      }
    })
  }

  const { track } = useTracking()
  const breakpoints = useBreakpoint()
  const baseTranslate = useTranslate('epr_settings.add_uin')
  const errorTranslate = useTranslate('error_state')
  const validationMessage = useFormValidationMessage(errors, 'epr_settings.add_uin')

  const handleFormSubmit = async () => {
    setShowGeneralError(false)
    setIsLoading(true)

    const formData = formMethods.getValues()

    if (!formData || !selectedCategoryId) {
      setIsLoading(false)

      return
    }

    const eprInfo: EprInfoModel = {
      epr_uin: {
        category: selectedCategoryId,
        uin: formData.uin,
      },
    }

    const response = await addEprInfo(eprInfo)

    if ('errors' in response) {
      setBackendErrors(response.errors)

      if (
        response.status === HttpStatus.InternalError ||
        response.status === HttpStatus.Unauthorized
      ) {
        setShowGeneralError(true)
      }

      setIsLoading(false)

      return
    }

    if (response.code === 0) {
      refetch()

      setIsLoading(false)

      track(
        clickEvent({
          target: 'submit_epr_uin',
          screen: 'epr_settings',
          targetDetails: JSON.stringify({
            category: formData.category,
            uin: formData.uin,
          }),
        }),
      )

      onClose()
    }
  }

  const handleFormCancel = () => {
    track(
      clickEvent({
        target: 'cancel_add_epr_uin',
        screen: 'epr_settings',
      }),
    )

    onCancel()
  }

  const countryInput =
    businessAccountFromUserDto(currentUser)?.address?.country_title || watch(AddUinField.Country)
  const categoryInput = watch(AddUinField.Category)

  const handleCountrySelect =
    (
      selectedCountry: EprCountryModel,
      inputRenderProps: InputDropdownRenderProps,
      input: AddUinField,
    ) =>
    () => {
      inputRenderProps.setIsSaveEnabled(true)

      setValue(input, selectedCountry.code, { shouldValidate: true, shouldDirty: true })

      if (breakpoints.phones) return

      inputRenderProps.closeDropdown()
    }

  const renderCountry = (
    countryOption: EprCountryModel,
    inputRenderProps: InputDropdownRenderProps,
    input: AddUinField,
  ) => {
    const isCountrySelected = countryOption.code === input
    const labelId = `label-id-${countryOption.title_local}`

    return (
      <Cell
        key={countryOption.code}
        title={countryOption.title_local}
        onClick={handleCountrySelect(countryOption, inputRenderProps, input)}
        id={labelId}
        suffix={
          <Radio
            name={`radio-${countryOption.title_local}`}
            checked={isCountrySelected}
            onChange={() => undefined}
            aria={{
              'aria-labelledby': labelId,
            }}
          />
        }
      />
    )
  }

  const renderCountriesList = (inputRenderProps: InputDropdownRenderProps) => (
    <List>
      {countries.map(country => renderCountry(country, inputRenderProps, AddUinField.Country))}
    </List>
  )

  const handleCategorySelect =
    (
      selectedCategory: CategoryOptions,
      inputRenderProps: InputDropdownRenderProps,
      input: AddUinField,
    ) =>
    () => {
      inputRenderProps.setIsSaveEnabled(true)

      setValue(input, selectedCategory.title, { shouldValidate: true, shouldDirty: true })
      setSelectedCategoryId(selectedCategory.id)

      if (breakpoints.phones) return

      inputRenderProps.closeDropdown()
    }

  const renderCategory = (
    categoryOption: CategoryOptions,
    inputRenderProps: InputDropdownRenderProps,
    input: AddUinField,
  ) => {
    const isCategorySelected = categoryOption.id === input
    const labelId = `id-${categoryOption.id}`

    return (
      <Cell
        key={categoryOption.id}
        title={categoryOption.title}
        body={categoryOption.examples}
        onClick={handleCategorySelect(categoryOption, inputRenderProps, input)}
        id={labelId}
        suffix={
          <Radio
            name={`radio-${categoryOption.title}`}
            checked={isCategorySelected}
            onChange={() => undefined}
            aria={{
              'aria-labelledby': labelId,
            }}
          />
        }
      />
    )
  }

  const renderCategoriesList = (inputRenderProps: InputDropdownRenderProps) => {
    if (!categories.length) return null

    return (
      <List>
        {categories.map(category =>
          renderCategory(category, inputRenderProps, AddUinField.Category),
        )}
      </List>
    )
  }

  const handleSubmitClick = (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    if (isLoading || isCategoriesLoading || isCountriesLoading) {
      event.preventDefault()

      return
    }

    handleFormSubmit()
  }

  const renderAddUinFormContent = () => {
    return (
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(handleFormSubmit)} noValidate className="container">
          <Cell>
            <SeparatedList separator={<Spacer size="x-large" />}>
              {!isDomesticEpr && (
                <Controller
                  name={AddUinField.Country}
                  render={({ field: { name } }) => (
                    <InputDropdown
                      isPhone={!!breakpoints.phones}
                      textSave={inputDropdownTranslate('actions.save')}
                      name={name}
                      disabled={isDomesticEpr}
                      styling="tight"
                      title={baseTranslate('country.title')}
                      placeholder={baseTranslate('country.placeholder')}
                      render={renderCountriesList}
                      value={countryInput}
                      validation={renderValidation(validationMessage(AddUinField.Country))}
                    />
                  )}
                  rules={{
                    required: true,
                  }}
                  control={control}
                />
              )}
              <Controller
                name={AddUinField.Category}
                render={({ field: { name } }) => (
                  <InputDropdown
                    isPhone={!!breakpoints.phones}
                    textSave={inputDropdownTranslate('actions.save')}
                    name={name}
                    readOnly
                    styling="tight"
                    title={baseTranslate('category.title')}
                    placeholder={baseTranslate('category.placeholder')}
                    render={renderCategoriesList}
                    value={categoryInput}
                    validation={renderValidation(validationMessage(AddUinField.Category))}
                  />
                )}
                rules={{
                  required: true,
                }}
                control={control}
              />
              <InputText
                {...register(AddUinField.Uin, {
                  required: true,
                })}
                styling="tight"
                title={baseTranslate('uin.title')}
                placeholder={baseTranslate('uin.placeholder')}
                validation={renderValidation(validationMessage(AddUinField.Uin))}
              />
            </SeparatedList>
            <Spacer size="x-large" />
            <Text as="h4" type="caption" text={baseTranslate('security_disclaimer')} />
            <Spacer size="x-large" />
            {showGeneralError && <Validation text={errorTranslate('title')} theme="warning" />}
            <Spacer size="small" />
            <Button
              text={baseTranslate('actions.submit')}
              onClick={handleSubmitClick}
              styling="filled"
              isLoading={isLoading || isCategoriesLoading || isCountriesLoading}
            />
            <Spacer size="medium" />
            <Button
              text={baseTranslate('actions.cancel')}
              onClick={handleFormCancel}
              styling="flat"
            />
          </Cell>
        </form>
      </FormProvider>
    )
  }

  return renderAddUinFormContent()
}

export default AddCountryUinForm
